import { differenceInMinutes, differenceInDays, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionDraftDto } from '@/Domain/api/Subscription/dto/SubscriptionDraft.dto';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';
import { formatCurrency } from '@/Domain/utils/formatCurrency';

type Props = {
  isPending: boolean;
  data: SubscriptionDraftDto | undefined;
};

const DAY_IN_MINUTES = 1440;
export const BillingSection = ({ data, isPending }: Props) => {
  const { t } = useTranslation('subscription_page');

  const nextPaymentLabel = useMemo(() => {
    if (data && !isPending) {
      const today = new Date();
      const start = parseISO(data.next_payment_on);

      const minutesDiff = differenceInMinutes(start, today);
      const dayDiff = differenceInDays(start, today);

      if (minutesDiff < DAY_IN_MINUTES) {
        return t('change_plan_dialog.less_than_one_day');
      }

      return t('change_plan_dialog.due_in_x_days', { days: dayDiff });
    }

    return undefined;
  }, [data?.next_payment_on]);

  if (isPending && !data) {
    return (
      <div className="flex flex-col gap-3 py-2">
        <section className="flex items-center justify-between">
          <Skeleton className="h-5 w-20 " />
          <Skeleton className="h-5 w-12 " />
        </section>
        <section className="flex items-center justify-between">
          <div className="flex items-center gap-0.5">
            <Skeleton className="h-5 w-24 " />
          </div>
          <Skeleton className="h-5 w-16 " />
        </section>
        {/* Reserve the refund/proration line so the dialog doesn't grow when the draft loads. */}
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 py-2">
      <section className="flex items-center justify-between">
        <p className=" text-foreground-subtle">{t('change_plan_dialog.due_today')}</p>
        <p className="text-foreground font-medium flex items-center gap-0.5">
          {data?.due_today ? formatCurrency(data.due_today) : formatCurrency(0)}
        </p>
      </section>
      <section className="flex items-center justify-between">
        <p className=" text-foreground-subtle flex gap-0.5 items-center">{nextPaymentLabel}</p>
        <p className="text-foreground font-medium flex items-center gap-0.5">
          {data?.next_payment ? formatCurrency(data.next_payment) : '-'}
        </p>
      </section>
      {data?.refund && (
        <p className="text-foreground-subtle text-xs">
          {t('change_plan_dialog.price_calculation', {
            refund: formatCurrency(data.refund),
            unusedDays: data.unused_days,
          })}
        </p>
      )}
    </div>
  );
};
